import { WorkOutline, Close } from "@mui/icons-material";
import CalendarJobs from "./CalendarJobs";
import { JobCard, CardDescription } from "./JobsWidgetWithWeek";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import { colors } from "../ui/colors";
import { useWidgetVariant } from "./useWidgetVariant";
import { RiveByKey } from "@components/riveAnimation";
import { is } from "zod/v4/locales";

export default function JobCalendarSeccion({
  open,
  selectedJobId,
  scrollContainerRef,
  jobsForList,
  handleOpen,
  selectedJob,
  jobsForCalendar,
  selectedDate,
  setSelectedDate,
  handleCloseDetail,
  timeZone,
}) {
  const { ref, isLargeSlot, width } = useWidgetVariant(600);

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: isLargeSlot ? "row" : "column",
        width: "100%",
        height: "80%",
        backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
        minHeight: isLargeSlot ? "400px" : "200px",
        maxHeight: "100%",
        mt: isLargeSlot ? 3 : 0,
      }}
    >
      <Box
        bgcolor={"#fff"}
        width={isLargeSlot ? "50%" : "100%"}
        marginTop={isLargeSlot ? 0 : -5}
        display={"flex"}
        alignContent={"center"}
        alignItems={"center"}
      >
        <CalendarJobs
          referenceDate={selectedDate || new Date()}
          jobs={jobsForCalendar}
          timeZone={timeZone}
          selectedDay={selectedDate}
          selectedJob="all"
          onSelectDay={(day) => {
            setSelectedDate(day);
            handleCloseDetail();

            setTimeout(() => {
              scrollContainerRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 100);
          }}
        />
      </Box>

      <Box
        sx={{
          display: isLargeSlot || (selectedDate && jobsForList.length > 0) ? "flex" : "none",
          flexDirection: "column",
          position: isLargeSlot ? "relative" : "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          pt: isLargeSlot ? 2.5 : 1.5,
          height: isLargeSlot ? "100%" : selectedDate ? "90%" : "200px",
          minHeight: isLargeSlot ? "100%" : "80%",
          width: isLargeSlot ? "50%" : "100%",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          zIndex: 10,
          borderRadius: isLargeSlot ? 0 : "16px 16px 0 0",
          transition: "0.3s ease",
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            color: colors.orange.base,
            fontWeight: 600,
            fontSize: "0.8rem",
            lineHeight: 1.2,
            my: 0.5,
            ml: 2,
            mt: 5,
          }}
        >
          {jobsForList.length === 0 ? "No tasks scheduled" : `You have ${jobsForList.length} ${jobsForList.length === 1 ? "task" : "tasks"}`}
        </Typography>

        {jobsForList.length > 0 && !open && !isLargeSlot && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setSelectedDate(null);
              handleCloseDetail();
            }}
            sx={{
              position: "absolute",
              top: 10,
              right: 20,
              color: "text.secondary",
              cursor: "pointer",
              zIndex: 20,
              p: 0,
              "&:hover": {
                backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            height: "100%",
            overflowY: "auto",
            mb: 5,
            px: 1.5,
          }}
        >
          {jobsForList.length > 0 ? (
            jobsForList.map((job) => <JobCard key={job.id} {...job} isSelected={selectedJobId === job.id} onOpen={handleOpen} />)
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "30%",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  border: `1px solid rgba(255, 152, 0, 0.15)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyItems: "center",
                  justifyContent: "center",
                  maxWidth: "320px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <RiveByKey riveKey="createjob" boxSize={isLargeSlot ? 200 : 250} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: colors.orange.base,
                    fontWeight: 800,
                    fontSize: "1rem",
                    textAlign: "center",
                    marginTop: -3,
                  }}
                >
                  Your day is wide open
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                >
                  There are no jobs scheduled for this day.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,

            height: open ? "63%" : "0%",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            zIndex: 100,
            transition: "0.4s ease-in-out",
            overflowY: "auto",
            borderRadius: "16px 16px 0 0",
            opacity: open ? 1 : 0,
            visibility: open ? "visible" : "hidden",
          }}
        >
          {open && selectedJob && (
            <CardDescription
              job={selectedJob}
              nameJob={selectedJob.title}
              idJob={selectedJob.serviceOrderId || selectedJob.jobId || selectedJob.id}
              statusJob={selectedJob.status}
              type={selectedJob.type}
              activeProgress={selectedJob.status?.toLowerCase() === "completed" ? 2 : selectedJob.status?.toLowerCase() === "inprogress" ? 1 : 0}
              onClose={handleCloseDetail}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
